import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Pagination,
  Modal,
  Spinner,
} from "react-bootstrap";
import { MdDelete, MdEdit } from "react-icons/md";
import { FaCopy } from "react-icons/fa";
import axios from "axios";
import { getURLbyEndPoint, getURLbyEndPointV2 } from "../../../store/api";
import Layout from "../../../components/Layouts/Layouts/Layout";
import MasterNavigationBar from "../../../components/Layouts/Layouts/MasterServicesLayout";
import DashboardHeader from "../../../components/basic/DashboardHeader";
import { toast } from "react-hot-toast";

const CreateEvent = () => {
  const [eventTable, setEventTable] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    eventType: "",
    eventDate: "",
    eventTime: "", // Reusing for startTime
    timeFrom: "",
    timeTo: "",
    fee: "", // Final Payable Fee
    actualPrice: "",
    discountedPrice: "",
    description: "",
    heading: "",
    location: "Online",
    speaker: "",
    duration: "",
    eCertificate: true,
  });

  const [showModal, setShowModal] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Video Modal State
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (name === "timeFrom" || name === "timeTo") {
      calculateDuration(
        name === "timeFrom" ? value : formData.timeFrom,
        name === "timeTo" ? value : formData.timeTo,
      );
    }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return;
    const startTime = new Date(`1970-01-01T${start}:00`);
    const endTime = new Date(`1970-01-01T${end}:00`);
    let diff = (endTime - startTime) / 1000 / 60; // diff in minutes

    if (diff < 0) diff += 24 * 60; // handle overnight events

    setFormData((prev) => ({ ...prev, duration: `${diff} min` }));
  };

  const handleReset = () => {
    setFormData({
      title: "",
      eventType: "",
      eventDate: "",
      eventTime: "",
      timeFrom: "",
      timeTo: "",
      fee: "",
      actualPrice: "",
      discountedPrice: "",
      description: "",
      heading: "",
      location: "Online",
      speaker: "",
      duration: "",
      eCertificate: true,
    });
    setIsFree(false);
    setEditId(null);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!formData.eventType || formData.eventType === "Select Type") {
        toast.error("Please select an event type");
        setLoading(false);
        return;
      }
      if (!formData.duration) {
        toast.error("Please select duration");
        setLoading(false);
        return;
      }
      if (!formData.speaker) {
        toast.error("Please enter speaker name");
        setLoading(false);
        return;
      }

      const apiFormData = new FormData();
      apiFormData.append("title", formData.title);
      apiFormData.append("eventType", formData.eventType);
      apiFormData.append("eventDate", formData.eventDate);
      apiFormData.append("eventTime", formData.timeFrom); // Set start time
      apiFormData.append("timeFrom", formData.timeFrom);
      apiFormData.append("timeTo", formData.timeTo);
      apiFormData.append("fee", isFree ? 0 : formData.fee);
      apiFormData.append("actualPrice", formData.actualPrice || 0);
      apiFormData.append("discountedPrice", formData.discountedPrice || 0);
      apiFormData.append("description", formData.description);
      apiFormData.append("heading", formData.heading);
      apiFormData.append("location", formData.location);
      apiFormData.append("speaker", formData.speaker);
      apiFormData.append("duration", formData.duration);
      apiFormData.append("eCertificate", formData.eCertificate);

      let response;
      if (editId) {
        response = await axios.put(
          getURLbyEndPoint("updateEvent", editId),
          apiFormData,
        );
      } else {
        response = await axios.post(
          getURLbyEndPoint("createEvent"),
          apiFormData,
        );
      }

      if (
        response.data.event ||
        response.status === 201 ||
        response.status === 200
      ) {
        handleReset();
        setShowModal(false);
        toast.success(response.data.message);
        getAllEvents();
      } else {
        toast.error(response.data.message || "Failed to process request");
      }
    } catch (error) {
      toast.error("Something went wrong!!");
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = (event) => {
    setEditId(event._id);
    setFormData({
      title: event.title || "",
      eventType: event.eventType || "",
      eventDate: event.eventDate ? event.eventDate.split("T")[0] : "",
      eventTime: event.eventTime || "",
      timeFrom: event.timeFrom || "",
      timeTo: event.timeTo || "",
      fee: event.fee || 0,
      description: event.description || "",
      heading: event.heading || "",
      location: event.location || "Online",
      speaker: event.speaker || "",
      duration: event.duration || "",
      eCertificate: event.eCertificate || false,
      actualPrice: event.actualPrice || 0,
      discountedPrice: event.discountedPrice || 0,
    });
    setIsFree(event.fee === 0);
    setShowModal(true);
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      const response = await axios.delete(getURLbyEndPoint("deleteEvent") + id);
      toast.success(response.data.message);
      getAllEvents();
    } catch (error) {
      toast.error("Something went wrong!!");
      console.log(error);
    }
  };

  const handleToggleRegistration = async (id, currentState) => {
    if (
      !window.confirm(
        `Are you sure you want to ${currentState ? "open" : "close"} registration for this event?`,
      )
    )
      return;
    try {
      const response = await axios.put(
        getURLbyEndPoint("closeEventRegistration") + id,
        {
          isRegistrationClosed: !currentState,
        },
      );
      if (response.data.status) {
        toast.success(response.data.message);
        getAllEvents();
      } else {
        toast.error(response.data.message || "Failed to toggle registration");
      }
    } catch (error) {
      toast.error("Something went wrong!!");
      console.log(error);
    }
  };

  const getAllEvents = async () => {
    try {
      const response = await axios.get(getURLbyEndPoint("getAllEvents"));
      const sortedEvents = response.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setEventTable(sortedEvents);
    } catch (error) {
      console.error("Error fetching Events:", error);
    }
  };

  const handleSyncRecording = async (meetingRoomId) => {
    try {
      const response = await axios.post(
        getURLbyEndPointV2("queryEventRecordingStatus"),
        { meetingRoomId },
      );
      if (response.data.status) {
        toast.success("Recording Status Synced");
        getAllEvents();
      } else {
        toast.error(response.data.message || "Failed to sync recording");
      }
    } catch (error) {
      console.error("Sync failed", error);
      toast.error(error.response?.data?.message || "Failed to sync recording");
    }
  };

  useEffect(() => {
    getAllEvents();
  }, []);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = eventTable.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(eventTable.length / itemsPerPage);

  const calculateRange = (currentPage, totalPages) => {
    let startPage = Math.max(1, currentPage - 5);
    let endPage = Math.min(totalPages, currentPage + 4);

    if (endPage - startPage < 9) {
      if (startPage === 1) {
        endPage = Math.min(10, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(totalPages - 9, 1);
      }
    }
    return { startPage, endPage };
  };
  const { startPage, endPage } = calculateRange(currentPage, totalPages);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(p + 1, totalPages));

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = { day: "numeric", month: "long", year: "numeric" };
    const datePart = date.toLocaleDateString("en-GB", options);
    const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
    return `${datePart} (${weekday})`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    h = h ? h : 12;
    return `${h}:${minutes} ${ampm} IST`;
  };

  return (
    <Layout>
      <MasterNavigationBar>
        <div className="d-flex flex-column px-3">
          <DashboardHeader
            heading="EVENTS / CHAMBER EVENT BOOKING / CREATE EVENT"
            backUrl="/chamber-event"
          />
          <hr />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Created Events</h4>
            <Button size="sm" onClick={() => setShowModal(true)}>
              Create Event
            </Button>
          </div>

          {/* Video Modal */}
          <Modal
            show={showVideoModal}
            onHide={() => {
              setShowVideoModal(false);
              setVideoUrl("");
            }}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Event Recording</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center bg-dark">
              {videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  style={{ width: "100%", maxHeight: "500px" }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <p className="text-white">Video not available</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <a
                href={videoUrl}
                download="Event_Recording.mp4"
                className="btn btn-success"
              >
                Download
              </a>
              <Button
                variant="secondary"
                onClick={() => setShowVideoModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showModal}
            onHide={() => {
              setShowModal(false);
              handleReset();
            }}
            centered
            size="md"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editId ? "Edit Event" : "Create Event"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleCreateEvent} onReset={handleReset}>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="heading">
                      <Form.Label>Heading</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        name="heading"
                        placeholder="Enter heading"
                        value={formData.heading}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="title">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        name="title"
                        placeholder="Enter title"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="eventType">
                      <Form.Label>Event Type</Form.Label>
                      <Form.Select
                        size="sm"
                        name="eventType"
                        value={formData.eventType}
                        onChange={handleChange}
                        aria-label="Event Type"
                      >
                        <option>Select Type</option>
                        <option value="Webinar">Webinar</option>
                        <option value="Workshop">Workshop</option>
                        <option value="Seminar">Seminar</option>
                        <option value="Live Session">Live Sessions</option>
                        <option value="Awareness Session">
                          Awareness Session
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group controlId="location">
                      <Form.Label>Mode (Online/Offline)</Form.Label>
                      <Form.Select
                        size="sm"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Form.Group controlId="speaker">
                      <Form.Label>Speaker</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        name="speaker"
                        placeholder="Enter Speaker Name"
                        value={formData.speaker}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-2 align-items-end">
                  <Col xs={12} md={4}>
                    <Form.Group controlId="eventPricing">
                      <Form.Label>Event Type & Pricing</Form.Label>
                      <Form.Select
                        size="sm"
                        name="isFree"
                        value={isFree ? "Free" : "Paid"}
                        onChange={(e) => {
                          const val = e.target.value === "Free";
                          setIsFree(val);
                          if (val) {
                            setFormData((prev) => ({
                              ...prev,
                              fee: 0,
                              discountedPrice: 0,
                            }));
                          } else {
                            setFormData((prev) => ({ ...prev, fee: "" }));
                          }
                        }}
                      >
                        <option value="Paid">Paid (Registration Fee)</option>
                        <option value="Free">Free of Charge</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="actualPrice">
                      <Form.Label>Actual Price (MRP)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        name="actualPrice"
                        placeholder="0.00"
                        value={formData.actualPrice}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="discountedPrice">
                      <Form.Label>Discounted / Final Fee</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        name="discountedPrice"
                        placeholder="0.00"
                        value={formData.discountedPrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            discountedPrice: val,
                            fee: val, // Syncing final fee with discounted price
                          }));
                        }}
                        disabled={isFree}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="eventDate">
                      <Form.Label>Event Date</Form.Label>
                      <Form.Control
                        size="sm"
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="timeFrom">
                      <Form.Label>Time From</Form.Label>
                      <Form.Control
                        size="sm"
                        type="time"
                        name="timeFrom"
                        value={formData.timeFrom}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group controlId="timeTo">
                      <Form.Label>Time To</Form.Label>
                      <Form.Control
                        size="sm"
                        type="time"
                        name="timeTo"
                        value={formData.timeTo}
                        onChange={handleChange}
                      />
                      {formData.duration && (
                        <div className="mt-1 small text-primary fw-bold">
                          Duration: {formData.duration}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Form.Group controlId="description">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        name="description"
                        rows={2}
                        placeholder="Enter Description"
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setShowModal(false);
                      handleReset();
                    }}
                    disabled={loading}
                  >
                    Close
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Processing...
                      </>
                    ) : editId ? (
                      "Update Event"
                    ) : (
                      "Save Event"
                    )}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          <div 
            className="mb-4 table-responsive" 
            style={{ overflowX: "auto", width: "100%", maxWidth: "100vw", WebkitOverflowScrolling: "touch" }}
          >
            <Table
              className="text-center align-middle"
              striped
              bordered
              hover
              style={{ minWidth: "1500px" }}
            >
              <thead>
                <tr>
                  <th style={{ fontSize: "14px", width: "50px" }}>Sr.No</th>
                  <th style={{ fontSize: "14px", width: "100px" }}>Type</th>
                  <th style={{ fontSize: "14px", width: "200px" }}>Title</th>
                  <th style={{ fontSize: "14px", width: "150px" }}>Speaker</th>
                  <th style={{ fontSize: "14px", width: "250px" }}>
                    Description
                  </th>
                  <th style={{ fontSize: "14px", width: "200px" }}>
                    Date & Duration
                  </th>
                  <th style={{ fontSize: "14px", width: "100px" }}>Fee</th>
                  <th style={{ fontSize: "14px", width: "120px" }}>
                    Admin Link
                  </th>
                  <th style={{ fontSize: "14px", width: "120px" }}>
                    Speaker Link
                  </th>
                  <th style={{ fontSize: "14px", width: "120px" }}>
                    User Link
                  </th>
                  <th style={{ fontSize: "14px", width: "120px" }}>
                    Form Link
                  </th>
                  <th style={{ fontSize: "14px", width: "120px" }}>
                    Recording
                  </th>
                  <th style={{ fontSize: "14px", width: "100px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={index}>
                      <td>{eventTable.length - indexOfFirstItem - index}</td>
                      <td>{item.eventType}</td>
                      <td>{item.title}</td>
                      <td>{item.speaker || "N/A"}</td>
                      <td>{item.description}</td>
                      <td>
                        <div>{formatDate(item.eventDate)}</div>
                        <div className="small text-muted">
                          {formatTime(item.eventTime)}
                        </div>
                        {item.duration && (
                          <div className="text-muted small">
                            {item.duration}
                          </div>
                        )}
                      </td>
                      <td>{item.fee}</td>
                      <td>
                        {item.joiningLink ? (
                          <a
                            href={`/meeting-room/${item.meetingId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-primary text-white text-decoration-none"
                            style={{ fontSize: "10px" }}
                          >
                            Join
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {item.joiningLink ? (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                `https://e-platform.aecci.org.in/speaker-join/${item.meetingId}`,
                              )
                            }
                            title="Copy Speaker Link"
                            style={{ fontSize: "10px" }}
                          >
                            <FaCopy /> Copy
                          </Button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        {item.joiningLink ? (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => copyToClipboard(item.joiningLink)}
                            title="Copy User Link"
                            style={{ fontSize: "10px" }}
                          >
                            <FaCopy /> Copy
                          </Button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                `https://e-platform.aecci.org.in/event-booking/${item._id}`,
                              )
                            }
                            title="Copy Form Link"
                            style={{ fontSize: "10px", padding: "2px 4px" }}
                          >
                            <FaCopy /> Copy
                          </Button>
                          <a
                            href={`https://e-platform.aecci.org.in/event-booking/${item._id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-info"
                            style={{ fontSize: "10px", padding: "2px 4px" }}
                          >
                            View
                          </a>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          {item.recordingFileUrl ? (
                            <Button
                              variant="success"
                              size="sm"
                              className="text-white"
                              style={{ fontSize: "10px", padding: "2px 4px" }}
                              onClick={() => {
                                setVideoUrl(item.recordingFileUrl);
                                setShowVideoModal(true);
                              }}
                            >
                              Watch
                            </Button>
                          ) : (
                            <span className="text-muted small">No Video</span>
                          )}
                          {item.meetingId && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() =>
                                handleSyncRecording(item.meetingId)
                              }
                              style={{ fontSize: "10px", padding: "2px 4px" }}
                            >
                              Sync Status
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => handleEditEvent(item)}
                            title="Edit"
                            style={{ padding: "4px 8px" }}
                          >
                            <MdEdit size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteEvent(item._id)}
                            title="Delete"
                            style={{ padding: "4px 8px" }}
                          >
                            <MdDelete size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              item.isRegistrationClosed
                                ? "outline-success"
                                : "outline-warning"
                            }
                            className="d-flex align-items-center justify-content-center"
                            onClick={() =>
                              handleToggleRegistration(
                                item._id,
                                item.isRegistrationClosed,
                              )
                            }
                            title={
                              item.isRegistrationClosed
                                ? "Open Registration"
                                : "Close Registration"
                            }
                            style={{ padding: "4px 8px", fontSize: "10px" }}
                          >
                            {item.isRegistrationClosed ? "Open" : "Close"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12">No Events Found</td>
                  </tr>
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-start mt-2">
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  />
                  {Array.from(
                    { length: endPage - startPage + 1 },
                    (_, index) => (
                      <Pagination.Item
                        key={index + startPage}
                        active={index + startPage === currentPage}
                        onClick={() => handlePageChange(index + startPage)}
                      >
                        {index + startPage}
                      </Pagination.Item>
                    ),
                  )}
                  <Pagination.Next
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </div>
        </div>
      </MasterNavigationBar>
    </Layout>
  );
};

export default CreateEvent;
